/**
 * What a prompt's answers say, and which ones are still on offer.
 *
 * Separated from the React that draws them, for the same reason `countdown.ts`
 * is: these are the rules, the component is four lines of wiring around them,
 * and rules that live inside an effect cannot be tested.
 *
 * ## Asking one argument at a time
 *
 * A move can carry more than one argument. Go Fish asks a named player for a
 * rank, and one row of every player crossed with every rank is dozens of
 * buttons for a decision that is really two.
 *
 * So a multi-argument prompt asks for the first, narrows the legal moves to
 * those beginning with that answer, and asks for the second. The list always
 * comes from `legalMoves`, so a combination the server would refuse can never
 * be reached — this file composes nothing, it only filters.
 */

import { describeArg, rankLabel, rankSpoken } from './cards.js';
import type { SeatInfo } from '@cards/protocol';

export type ArgKind = 'text' | 'rank' | 'suit' | 'seat' | 'amount';

export interface PromptSpec {
  move: string;
  label: string;
  unit?: string;
  /** What pressing it does, so it can be coloured. See the toolkit's note. */
  tone?: 'go' | 'quit' | 'commit';
  /** What each argument is. Absent means "draw it the way we always did". */
  args?: readonly ArgKind[];
  /** The question for each argument after the first. `{name}` is the last answer. */
  then?: readonly string[];
  /** The verb on the button that commits an amount. "Raise to", "Bet". */
  commit?: string;
  labels?: Record<string, string>;
}

/** A range of legal amounts, for a move whose answers cannot be listed. */
export interface PromptRange {
  min: number;
  max: number;
  step: number;
  presets?: { label: string; value: number }[];
}

/** A move the server said is legal right now. */
export interface PromptMove {
  move: string;
  args: string[];
  range?: PromptRange;
}

/** What one button says, and how a screen reader says it. */
export interface Answer {
  value: string;
  shown: string;
  spoken: string;
  /** Only suits set this, and only the two red ones set it true. */
  red?: boolean;
  /** Drawn as a glyph over a word rather than as a plain label. */
  suit?: { mark: string; word: string };
}

function asSuit(value: string): Answer | null {
  const suit = describeArg(value);
  if (!suit) return null;
  const [mark, word] = suit.text.split(' ');
  return {
    value,
    shown: suit.text,
    spoken: suit.text,
    red: suit.red,
    suit: { mark: mark ?? value, word: word ?? '' },
  };
}

/**
 * Turn one raw argument into something a person can read.
 *
 * A per-value label wins over every general rule, because a game naming an
 * answer knows something the renderer cannot: Spades' nil is a bid of zero, and
 * no rule about numbers would ever produce "Nil".
 */
function describeAnswer(
  value: string,
  kind: ArgKind,
  spec: PromptSpec,
  seats: readonly SeatInfo[],
): Answer {
  const named = spec.labels?.[value];
  if (named !== undefined) return { value, shown: named, spoken: named };

  if (kind === 'seat') {
    const seat = seats.find((info) => info.seat === Number(value));
    const name = seat?.displayName ?? `Seat ${String(Number(value) + 1)}`;
    return { value, shown: name, spoken: name };
  }

  if (kind === 'rank') {
    return { value, shown: rankLabel(value), spoken: rankSpoken(value) };
  }

  if (kind === 'suit') {
    const suit = asSuit(value);
    if (suit) return suit;
  }

  /*
   * No descriptor, or one that did not apply.
   *
   * A suit letter is still recognised, because knowing what a suit is, is card
   * knowledge rather than game knowledge — and this is exactly how every prompt
   * behaved before `args` existed, which is what keeps older layouts working
   * without being touched.
   */
  const suit = asSuit(value);
  if (suit) return suit;

  return { value, shown: value, spoken: spec.unit ? `${value} ${spec.unit}` : value };
}

/**
 * How many answers this prompt needs.
 *
 * From the declaration when there is one; otherwise from the widest move the
 * server offered, which is how prompts worked before `args` existed.
 */
export function argCount(spec: PromptSpec, options: readonly PromptMove[]): number {
  if (spec.args) return spec.args.length;

  let most = 0;
  for (const option of options) {
    most = Math.max(most, option.args.length);
    /*
     * A move carrying a range needs an answer even with no args on it yet.
     *
     * Without this, a game that sends a range but forgets to declare
     * `args: ['amount']` falls through to the argument-free shape and renders a
     * button that would submit a raise with no amount. Counting the range means
     * it renders nothing instead — a visible bug rather than a malformed move,
     * and the safer of the two ways to be wrong.
     */
    if (option.range && most < 1) most = 1;
  }
  return most;
}

/**
 * How a prompt is drawn: one button, or a row of choices.
 *
 * Named and exported because forgetting it was a real bug. A move carrying no
 * arguments has nothing to offer, so asking for the answers at index 0 returns
 * an empty list — and an empty list renders as nothing at all. Cheat's "Do you
 * believe them?" and Rummy's two draws vanished, leaving a player whose turn it
 * was with no way to act.
 *
 * Both games declared it correctly; the renderer stopped honouring it. A branch
 * inside a component cannot be tested here, so the decision lives out where it
 * can be.
 */
export function promptShape(
  spec: PromptSpec,
  options: readonly PromptMove[],
): 'button' | 'choices' | 'range' {
  /*
   * Declared, not sniffed (D71).
   *
   * The range values have to come from the move — they depend on what a seat
   * has left — but WHETHER a prompt is a range is the game's declaration, the
   * same way every other prompt shape is. A panel that decided by looking for a
   * `range` field would be guessing, and would guess wrong the first time a
   * game sent one it did not mean as the whole answer.
   */
  if (spec.args?.indexOf('amount') === 0 && rangeOf(options) !== null) return 'range';
  return argCount(spec, options) === 0 ? 'button' : 'choices';
}

/**
 * The interval this prompt offers, or null if it offers none.
 *
 * Takes the first option carrying one: a prompt whose answer is an amount has
 * exactly one move behind it, because the amount IS the choice.
 */
export function rangeOf(options: readonly PromptMove[]): PromptRange | null {
  for (const option of options) {
    const range = option.range;
    if (!range) continue;
    if (!Number.isFinite(range.min) || !Number.isFinite(range.max)) continue;
    if (range.max < range.min) continue;
    return range;
  }
  return null;
}

/**
 * Amounts worth reaching in one tap, with anything outside the range dropped.
 *
 * A game names these — "Pot", "All in" — because which amounts matter is game
 * knowledge. Filtering here rather than trusting them means a preset that has
 * gone stale (a pot-sized bet larger than the stack behind it) is quietly
 * dropped instead of offering a player a move the server would refuse.
 */
export function presetsOf(range: PromptRange): Array<{ label: string; value: number }> {
  const out: Array<{ label: string; value: number }> = [];
  for (const preset of range.presets ?? []) {
    if (preset.value < range.min || preset.value > range.max) continue;
    if (out.some((seen) => seen.value === preset.value)) continue;
    out.push(preset);
  }
  return out;
}

/** Snap an amount to the range: inside it, and on a step. */
export function clampToRange(range: PromptRange, value: number): number {
  const step = range.step > 0 ? range.step : 1;
  const stepped = range.min + Math.round((value - range.min) / step) * step;
  if (stepped <= range.min) return range.min;
  if (stepped >= range.max) return range.max;
  return stepped;
}

/**
 * The question to put at `index`, given what has been chosen already.
 *
 * The first is the prompt's own label. A later one comes from `then`, with
 * `{name}` replaced by the previous answer as it was drawn — so a two-part
 * prompt reads as two questions rather than a question and a dangling name.
 *
 * Without `then` it falls back to naming what was chosen, which is what the
 * browser showed before this existed: "Player F59C…", an answer pretending to
 * be a question.
 */
export function questionAt(
  spec: PromptSpec,
  chosen: readonly string[],
  seats: readonly SeatInfo[],
): string {
  if (chosen.length === 0) return spec.label;

  const last = chosen[chosen.length - 1] as string;
  const kind = spec.args?.[chosen.length - 1] ?? 'text';
  const answered = describeAnswer(last, kind, spec, seats).shown;

  const asked = spec.then?.[chosen.length - 1];
  if (asked === undefined) return `${answered}…`;
  return asked.replace('{name}', answered);
}

/**
 * Whether this prompt has anything to draw at all.
 *
 * Asked before the band is drawn, and the band is not drawn if nothing answers
 * yes. One shared band means one empty box is now possible where per-prompt
 * boxes could only ever be absent: a game that sends a range but forgets to
 * declare `args: ['amount']` offers a move with no arguments and no answers,
 * the group draws nothing, and the band around it would be a lifted panel with
 * nothing inside it.
 */
export function promptDraws(
  spec: PromptSpec,
  options: readonly PromptMove[],
  seats: readonly SeatInfo[],
): boolean {
  const shape = promptShape(spec, options);
  if (shape === 'button') return true;
  if (shape === 'range') return rangeOf(options) !== null;
  return answersAt(options, [], 0, spec, seats).length > 0;
}

/** The distinct answers available at `index`, given what has been chosen already. */
export function answersAt(
  options: readonly PromptMove[],
  chosen: readonly string[],
  index: number,
  spec: PromptSpec,
  seats: readonly SeatInfo[],
): Answer[] {
  const kind = spec.args?.[index] ?? 'text';
  const seen = new Set<string>();
  const answers: Answer[] = [];

  for (const option of options) {
    let matches = true;
    for (let i = 0; i < chosen.length; i++) {
      if (option.args[i] !== chosen[i]) matches = false;
    }
    if (!matches) continue;

    const value = option.args[index];
    if (value === undefined || seen.has(value)) continue;
    seen.add(value);
    answers.push(describeAnswer(value, kind, spec, seats));
  }
  return answers;
}
